import { Component, Input, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { takeUntil, finalize } from 'rxjs/operators';
import {
	RestService,
	IS_EMPTY,
	DIFFERENCE_FROM_NOW,
	IS_NOT_NULL_OR_UNDEFINED,
	InfractionDetails,
	IS_EMPTY_LIST,
	IS_NOT_EMPTY_LIST,
} from 'cargo-chief-commons';

import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { UrlService } from '@service/url/url.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { UtilService } from '@service/util/util.service';
import { BaseComponent } from '@shared/base/base.component';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { SharedService } from '@service/shared/shared.service';
import { environment } from '@environment/environment';

@Component({
	selector: 'app-quality',
	templateUrl: './quality.component.html',
	styleUrls: ['./quality.component.scss'],
})
export class QualityComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() carrierId!: string;
	@Input() mc!: string;
	@Input() dot!: string;
	@Input() destinationCity!: string;
	@Input() destinationState!: string;
	@Input() originCity!: string;
	@Input() originState!: string;
	@Input() isCarrierInNetwork: boolean;
	@Input() ccBookingRateDisplay: string;
	@Input() ccEngagementRateDisplay: string;
	@Input() ccFalloffRateDisplay: string;
	@Input() ccTotalBookedLoads: number;
	@Input() ccScore: number;
	@Input() vettingOverallDisplay: string;
	@Input() vettingAuthorityDisplay: string;
	@Input() vettingInsuranceDisplay: string;
	@Input() vettingOperationsDisplay: string;
	@Input() vettingSafetyDisplay: string;
	@Input() vettingOtherDisplay: string;
	@Input() infractionDetails: InfractionDetails = {} as InfractionDetails;
	@Input() lastVettingTime: Date;
	@Input() carrierActive: boolean;

	isLoadingQualityScoreDetails = false;

	ccScoreDisplay: string;
	isLoadingCarriersLoadsList: boolean = false;
	isLoadingCarrierQualityFromMcp: boolean = false;
	mcpCarrierQuality!: any;
	allInfractions: string[] = [];

	IS_EMPTY_LIST = IS_EMPTY_LIST;
	IS_NOT_EMPTY_LIST = IS_NOT_EMPTY_LIST;

	constructor(
		public carrierVettingService: CarrierVettingService,
		private restService: RestService,
		private urlService: UrlService,
		private cargochief: Cargochief,
		private sharedService: SharedService,
		public utilService: UtilService
	) {
		super();
	}

	ngOnInit() {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.carrierId) {
			if (Number.isInteger(this.ccScore)) {
				this.ccScoreDisplay = this.sharedService.getCarrierScoreToDisplay(
					this.ccScore,
					this.carrierActive
				);
			} else {
				this.ccScoreDisplay = 'N/A';
			}
		}
		this.combineAllInfractions();
		const noVettingIntegrationIsEnabled =
			!this.carrierVettingService.isRMISEnabled() && !this.carrierVettingService.isMCPEnabled();
		const mcpEnabledAndVettingTimeBeyondThreshold =
			this.carrierVettingService.isMCPEnabled() &&
			IS_NOT_NULL_OR_UNDEFINED(this.lastVettingTime) &&
			DIFFERENCE_FROM_NOW(this.lastVettingTime, 'hours') > environment.VETTING_TIME_THRESHOLD;
		if (noVettingIntegrationIsEnabled || mcpEnabledAndVettingTimeBeyondThreshold) {
			this.getMcpCarrierQuality();
		}
	}

	getMcpCarrierQuality() {
		if (IS_EMPTY(this.mc) && IS_EMPTY(this.dot)) {
			return;
		}
		this.isLoadingCarrierQualityFromMcp = true;
		let token = '';
		this.mcpCarrierQuality = null;
		if (this.carrierVettingService.isMCPEnabled()) {
			const mcp = this.cargochief.MY_CARRIER_PACKETS;
			token = mcp.data ? JSON.parse(mcp.data)?.token : '';
		}
		const url = this.urlService.getMcpCarrierQuality(
			this.mc,
			this.dot,
			this.isCarrierInNetwork ? this.isCarrierInNetwork : false,
			this.carrierId
		);
		this.restService
			.GET(url)
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.isLoadingCarrierQualityFromMcp = false;
				})
			)
			.subscribe(
				(response) => {
					this.mcpCarrierQuality = response.data;
					this.vettingOverallDisplay = this.mcpCarrierQuality?.overall;
					this.vettingAuthorityDisplay = this.mcpCarrierQuality?.authority;
					this.vettingInsuranceDisplay = this.mcpCarrierQuality?.insurance;
					this.vettingOperationsDisplay = this.mcpCarrierQuality?.operations;
					this.vettingSafetyDisplay = this.mcpCarrierQuality?.safety;
					this.vettingOtherDisplay = this.mcpCarrierQuality?.other;
					this.infractionDetails = this.mcpCarrierQuality?.infractionDetails || {};
					this.combineAllInfractions();
				},
				(error) => {
					this.utilService.showSnackbar(
						`Unable to get carrier quality`,
						SNACKBAR.WARNING,
						SNACKBAR.ACTION
					);
				}
			);
	}

	getOverallDisplayClass() {
		if (['acceptable', 'certified'].includes(this.vettingOverallDisplay?.toLowerCase()?.trim())) {
			return 'Acceptable';
		} else if (
			['unacceptable', 'not certified'].includes(this.vettingOverallDisplay?.toLowerCase()?.trim())
		) {
			return 'Unacceptable';
		}
		return '';
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	getQualityClass(quality) {
		if (quality) {
			return quality;
		} else if (!this.carrierActive) {
			return 'Unacceptable';
		}
		return '';
	}

	getQualityDisplay(quality) {
		if (quality) {
			return quality;
		} else if (!this.carrierActive) {
			return 'Unacceptable';
		}
		return 'N/A';
	}

	getCCOverallClass(score) {
		if (score && score !== 'N/A') {
			return score;
		} else if (!this.carrierActive) {
			return 'Low';
		}
		return 'N/A';
	}

	getCCOverallDisplay(score) {
		if (score && score !== 'N/A') {
			return score;
		} else if (!this.carrierActive) {
			return 'Low';
		}
		return 'N/A';
	}

	tootltipMessage(infractions: string[]): string {
		if (IS_NOT_EMPTY_LIST(infractions)) {
			return infractions.join('\n');
		}
	}

	combineAllInfractions() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.infractionDetails)) {
			this.allInfractions = [];
			if (IS_NOT_NULL_OR_UNDEFINED(this.infractionDetails.authority)) {
				this.allInfractions.push(...this.infractionDetails.authority);
			}
			if (IS_NOT_NULL_OR_UNDEFINED(this.infractionDetails.insurance)) {
				this.allInfractions.push(...this.infractionDetails.insurance);
			}
			if (IS_NOT_NULL_OR_UNDEFINED(this.infractionDetails.operations)) {
				this.allInfractions.push(...this.infractionDetails.operations);
			}
			if (IS_NOT_NULL_OR_UNDEFINED(this.infractionDetails.safety)) {
				this.allInfractions.push(...this.infractionDetails.safety);
			}
			if (IS_NOT_NULL_OR_UNDEFINED(this.infractionDetails.other)) {
				this.allInfractions.push(...this.infractionDetails.other);
			}
		}
	}
}
